import React, { useState } from 'react';
import { BillingDocument } from '../../types/billing';
import apiClient from '../../services/apiClient';

interface Props {
  documents: BillingDocument[];
}

/**
 * Format date to Polish format (DD.MM.YYYY)
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Format money amount with currency
 */
const formatMoney = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'code',
  }).format(amount);
};

/**
 * Document Type Badge Component
 */
const DocumentTypeBadge: React.FC<{ type: 'invoice' | 'proforma' }> = ({
  type,
}) => {
  const config = {
    invoice: { label: 'Faktura VAT', style: 'bg-green-100 text-green-800' },
    proforma: { label: 'Proforma', style: 'bg-blue-100 text-blue-800' },
  };

  const { label, style } = config[type];

  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}
      aria-label={`Typ dokumentu: ${label}`}
    >
      {label}
    </span>
  );
};

/**
 * Document Status Badge Component
 */
const DocumentStatusBadge: React.FC<{
  status: string;
  dueDate?: string;
}> = ({ status, dueDate }) => {
  const config: Record<string, { label: string; style: string }> = {
    paid: { label: 'Opłacona', style: 'bg-green-100 text-green-800' },
    issued: { label: 'Wystawiona', style: 'bg-yellow-100 text-yellow-800' },
    overdue: { label: 'Przeterminowana', style: 'bg-red-100 text-red-800' },
    canceled: { label: 'Anulowana', style: 'bg-neutral-100 text-neutral-600' },
  };

  const { label, style } = config[status] || config.issued;

  return (
    <div className="flex flex-col">
      <span
        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}
        aria-label={`Status: ${label}`}
      >
        {label}
      </span>
      {status === 'issued' && dueDate && (
        <span className="text-xs text-neutral-500 mt-1">
          Termin: {formatDate(dueDate)}
        </span>
      )}
    </div>
  );
};

/**
 * Extract filename from Content-Disposition header, fall back to provided default.
 */
const extractFilename = (headers: Record<string, unknown>, fallback: string): string => {
  const disposition = String(headers?.['content-disposition'] ?? '');
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return match?.[1] ?? fallback;
};

/**
 * Download PDF via authenticated API call (for proxy URLs like /billing/self-service/documents/:id/pdf)
 */
const handlePdfDownload = async (url: string, fallbackFilename: string) => {
  try {
    const response = await apiClient.get(url, { responseType: 'blob' });
    const filename = extractFilename(response.headers, fallbackFilename);
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Silently fail - user sees no download
  }
};

/**
 * Returns true if the URL is a relative proxy URL that needs authenticated fetch
 */
const isProxyUrl = (url: string): boolean => url.startsWith('/');

/**
 * PDF download link - uses blob download for proxy URLs, direct link for external URLs.
 * Shows "Pobieranie..." and disables button while fetching.
 */
const PdfDownloadLink: React.FC<{
  url: string | null;
  number: string;
  className: string;
  label: string;
}> = ({ url, number, className, label }) => {
  const [downloading, setDownloading] = useState(false);

  // Legacy invoices may have no PDF (pdf_url NULL / empty). Render a disabled
  // state instead of crashing on isProxyUrl(null).startsWith('/').
  if (!url) {
    return (
      <span
        className="text-neutral-400 text-sm cursor-not-allowed"
        aria-label={`PDF niedostępny dla dokumentu ${number}`}
      >
        PDF niedostępny
      </span>
    );
  }

  if (isProxyUrl(url)) {
    return (
      <button
        disabled={downloading}
        onClick={async () => {
          setDownloading(true);
          try {
            await handlePdfDownload(url, `${number}.pdf`);
          } finally {
            setDownloading(false);
          }
        }}
        className={`${className} ${downloading ? 'opacity-50 cursor-not-allowed' : ''}`}
        aria-label={label}
      >
        {downloading ? 'Pobieranie...' : 'Pobierz PDF'}
      </button>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      aria-label={label}
    >
      Pobierz PDF
    </a>
  );
};

/**
 * Billing Documents List Component
 *
 * Displays a list of billing documents (invoices and proformas).
 * Responsive: table on desktop, cards on mobile.
 */
export const BillingDocumentsList: React.FC<Props> = ({ documents }) => {
  return (
    <>
      {/* Desktop Table (hidden on mobile) */}
      <div className="hidden md:block bg-white shadow-sm rounded-lg border border-neutral-200">
        <div className="overflow-x-auto">
          <table
            className="w-full"
            aria-label="Lista dokumentow rozliczeniowych"
          >
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Typ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Numer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Data wystawienia
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Kwota brutto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4">
                    <DocumentTypeBadge type={doc.type} />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">
                    {doc.number}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {formatDate(doc.issueDate)}
                  </td>
                  <td className="px-6 py-4 text-sm font-mono text-neutral-900">
                    {formatMoney(doc.amountGross, doc.currency)}
                  </td>
                  <td className="px-6 py-4">
                    <DocumentStatusBadge
                      status={doc.status}
                      dueDate={doc.dueDate}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <PdfDownloadLink
                        url={doc.pdfUrl}
                        number={doc.number}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        label={`Pobierz PDF faktury ${doc.number}`}
                      />
                      <span className="text-neutral-300">|</span>
                      <a
                        href={doc.viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-neutral-600 hover:text-neutral-800 text-sm"
                        aria-label={`Podgląd faktury ${doc.number} w Fakturownia`}
                      >
                        Podgląd
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards (hidden on desktop) */}
      <div className="md:hidden space-y-4">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm"
          >
            <div className="flex items-start justify-between mb-2">
              <DocumentTypeBadge type={doc.type} />
              <DocumentStatusBadge status={doc.status} dueDate={doc.dueDate} />
            </div>
            <div className="font-medium text-neutral-900 mb-1">{doc.number}</div>
            <div className="text-sm text-neutral-600 mb-3">
              {formatDate(doc.issueDate)} &bull;{' '}
              {formatMoney(doc.amountGross, doc.currency)}
            </div>
            <div className="flex gap-2">
              <PdfDownloadLink
                url={doc.pdfUrl}
                number={doc.number}
                className="flex-1 text-center px-3 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
                label={`Pobierz PDF ${doc.number}`}
              />
              <a
                href={doc.viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center px-3 py-2 border border-neutral-300 text-neutral-700 rounded-md text-sm font-medium hover:bg-neutral-50"
                aria-label={`Podgląd dokumentu ${doc.number}`}
              >
                Podgląd
              </a>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
